// StandaloneCustomerPdfGeneratorForTable.jsx (New name to reflect its purpose)
import React, { useRef, useState } from "react"; // Removed useState, useEffect as they are not needed for this specific render output
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image,
  Font,
  PDFViewer,
  PDFDownloadLink,
  pdf,
} from "@react-pdf/renderer";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf"; // For the button icon
import { Tooltip, IconButton, CircularProgress } from "@mui/material"; // For the button styling
import { useEffect } from "react";

// --- FONT REGISTRATION (Optional but Recommended) ---
/*
Font.register({
  family: 'Open Sans',
  fonts: [
    { src: '/fonts/OpenSans-Regular.ttf' },
    { src: '/fonts/OpenSans-Bold.ttf', fontWeight: 'bold' },
  ],
});
Font.registerHyphenationCallback(word => [word]);
*/

// --- PDF DOCUMENT STRUCTURE AND STYLING (CrmPdfDocument) ---
// This CrmPdfDocument definition remains EXACTLY THE SAME as the previous version
// It includes all the styles, InfoPair, formatDate, formatCurrency, and the full PDF layout.
// I'm omitting it here for brevity, but assume it's the same long CrmPdfDocument component.
const CrmPdfDocument = ({ customerData }) => {
  const styles = StyleSheet.create({
    page: {
      fontFamily: "Helvetica",
      fontSize: 9,
      paddingTop: 75,
      paddingBottom: 60,
      paddingHorizontal: 30,
      lineHeight: 1.35,
      color: "#2C3E50",
    },
    header: {
      position: "absolute",
      top: 15,
      left: 30,
      right: 30,
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      borderBottomWidth: 1.5,
      borderBottomColor: "#3498DB",
      paddingBottom: 5,
    },
    logo: { width: 120, height: 40, objectFit: "contain" },
    companyDetailsHeader: { textAlign: "right", fontSize: 8, color: "#34495E" },
    companyNameHeader: {
      fontSize: 13,
      fontWeight: "bold",
      color: "#2980B9",
      fontFamily: "Helvetica-Bold",
    },
    footer: {
      position: "absolute",
      bottom: 25,
      left: 30,
      right: 30,
      textAlign: "center",
      color: "#7F8C8D",
      fontSize: 8,
      flexDirection: "row",
      justifyContent: "space-between",
    },
    documentTitle: {
      textAlign: "center",
      fontSize: 12,
      fontWeight: "bold",
      fontFamily: "Helvetica-Bold",
      marginTop: 10,
      marginBottom: 10,
      color: "#2980B9",
      textTransform: "uppercase",
      letterSpacing: 1,
    },
    sectionTitle: {
      fontSize: 13,
      fontWeight: "bold",
      fontFamily: "Helvetica-Bold",
      backgroundColor: "#ECF0F1",
      paddingVertical: 4,
      paddingHorizontal: 7,
      marginBottom: 4,
      color: "#2C3E50",
      borderLeftWidth: 3,
      borderLeftColor: "#3498DB",
      marginTop: 8,
    },
    subsectionTitle: {
      fontSize: 10,
      fontWeight: "bold",
      fontFamily: "Helvetica-Bold",
      color: "#2980B9",
      marginBottom: 2,
      marginTop: 4,
      borderBottomWidth: 0.5,
      borderBottomColor: "#BDC3C7",
      paddingBottom: 1,
    },
    label: {
      fontWeight: "bold",
      fontFamily: "Helvetica-Bold",
      color: "#34495E",
      minWidth: 90,
    },
    value: { color: "#2C3E50", flexShrink: 1 },
    smallText: { fontSize: 8, color: "#7F8C8D" },
    grid: {
      flexDirection: "row",
      flexWrap: "wrap",
      justifyContent: "space-between",
      marginBottom: 3,
    },
    gridItem: { width: "48%", marginBottom: 2 },
    infoRow: { flexDirection: "row", marginBottom: 2 },
    table: {
      display: "table",
      width: "auto",
      borderStyle: "solid",
      borderWidth: 0.5,
      borderColor: "#BDC3C7",
      marginBottom: 6,
    },
    tableRow: { flexDirection: "row", backgroundColor: "#FFFFFF" },
    tableRowAlt: { flexDirection: "row", backgroundColor: "#F8F9F9" },
    tableColHeader: {
      borderStyle: "solid",
      borderWidth: 0.5,
      borderColor: "#BDC3C7",
      borderLeftWidth: 0,
      borderTopWidth: 0,
      backgroundColor: "#E0E6E9",
      padding: 3,
      textAlign: "center",
      fontWeight: "bold",
      fontFamily: "Helvetica-Bold",
      fontSize: 8.5,
      color: "#2C3E50",
    },
    tableCol: {
      borderStyle: "solid",
      borderWidth: 0.5,
      borderColor: "#BDC3C7",
      borderLeftWidth: 0,
      borderTopWidth: 0,
      padding: 3,
      fontSize: 8,
    },
    deliverySection: {
      marginBottom: 10,
      padding: 8,
      borderWidth: 1,
      borderColor: "#AED6F1",
      borderRadius: 3,
      backgroundColor: "#FDFEFE",
    },
    notesBox: {
      borderWidth: 0.5,
      borderColor: "#D5DBDB",
      padding: 6,
      backgroundColor: "#FBFCFC",
      marginTop: 3,
      borderRadius: 3,
    },
    highlight: {
      color: "#E74C3C",
      fontWeight: "bold",
      fontFamily: "Helvetica-Bold",
    },
    subtleHighlight: {
      backgroundColor: "#FEF9E7",
      paddingHorizontal: 3,
      paddingVertical: 1,
      borderRadius: 2,
    },
    currency: { textAlign: "right" },
  });

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch (e) {
      return dateString;
    }
  };
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return "N/A";
    return new Intl.NumberFormat("en-IN", {
      maximumFractionDigits: 0,
    }).format(amount);
  };
  const InfoPair = ({ label, value, style, valueStyle }) => (
    <View style={[styles.infoRow, style]}>
      <Text style={styles.label}>{label}: </Text>
      <Text style={[styles.value, valueStyle]}>{value || "N/A"}</Text>
    </View>
  );

  // Ensure customerData and its nested properties exist before accessing them
  const cd = customerData || {};
  const primaryContact = cd.primaryContact || {};
  const address = cd.address || {};
  const salesPersonDetail = cd.salesPersonDetail || {};
  const modelDetail = cd.modelDetail || {};
  const oldDetail = cd.oldDetail || {};
  const deliveries = cd.deliveries || [];
  const communicationLog = cd.communicationLog || [];
  const servicedEquipment = cd.servicedEquipment || [];
  const customerExistingFleet = cd.customerExistingFleet || [];
  const serviceHistory = cd.serviceHistory || [];
  const marketingPreferences = cd.marketingPreferences || {};
  const customerPaymentPreferences = cd.customerPaymentPreferences || {};

  return (
    <Document
      title={`Customer Dossier - ${cd.fullName || "N/A"}`}
      author="New Keshav Tractors"
    >
      <Page size="A4" style={styles.page} wrap>
        <View style={styles.header} fixed>
          <Image
            style={styles.logo}
            src="https://ui-avatars.com/api/?name=Keshav&background=random&size=120"
          />
          <View style={styles.companyDetailsHeader}>
            <Text style={styles.companyNameHeader}>New Keshav Tractors</Text>
            <Text>Dhrangadhra, Gujarat 363310</Text>
            <Text>harilalsonalika@gmail.com</Text>
          </View>
        </View>
        <Text
          style={styles.footer}
          fixed
          render={({ pageNumber, totalPages }) => (
            <Text>
              Page {pageNumber} of {totalPages} | Generated:{" "}
              {formatDate(new Date().toISOString())} | CustID:{" "}
              {cd.customerId || "N/A"}
            </Text>
          )}
        />
        <Text style={styles.documentTitle}>Customer Comprehensive Report</Text>

        <Text style={styles.sectionTitle}>Customer Profile</Text>
        <View style={styles.grid}>
          <InfoPair
            label="Full Name"
            value={cd.fullName}
            style={styles.gridItem}
          />
          <InfoPair
            label="Customer ID"
            value={cd.customerId}
            style={styles.gridItem}
          />
        </View>
        <Text style={styles.subsectionTitle}>Primary Contact</Text>
        <View style={styles.grid}>
          <InfoPair
            label="Contact Person"
            value={primaryContact.name}
            style={styles.gridItem}
          />
          <InfoPair
            label="Phone"
            value={primaryContact.phone}
            style={styles.gridItem}
          />
          <InfoPair
            label="WhatsApp"
            value={primaryContact.whatsapp}
            style={styles.gridItem}
          />
          <InfoPair
            label="Preferred Contact"
            value={primaryContact.preferredContactMethod}
            style={styles.gridItem}
          />
        </View>
        <Text style={styles.subsectionTitle}>Area Details</Text>
        <View style={styles.grid}>
          <InfoPair
            label="Village"
            value={address.village}
            style={styles.gridItem}
          />
          <InfoPair
            label="Taluka"
            value={address.taluka}
            style={styles.gridItem}
          />
          <InfoPair
            label="District"
            value={address.district}
            style={styles.gridItem}
          />
          <InfoPair
            label="State"
            value={`${address.state || ""}`}
            style={styles.gridItem}
          />
        </View>
        <Text style={styles.subsectionTitle}>Salesperson Detail</Text>
        <View style={styles.grid}>
          <InfoPair
            label="Salesperson"
            value={`${salesPersonDetail.name || ""} (${
              salesPersonDetail.employeeId || ""
            })`}
            style={styles.gridItem}
          />
          <InfoPair
            label="Phone"
            value={salesPersonDetail.contact}
            style={styles.gridItem}
          />
          <InfoPair
            label="Email"
            value={salesPersonDetail.email}
            style={styles.gridItem}
          />
          <InfoPair
            label="Created by"
            value={salesPersonDetail.created_by}
            style={styles.gridItem}
          />
        </View>
        <Text style={styles.subsectionTitle}>New Model Detail</Text>
        <View style={styles.grid}>
          <InfoPair
            label="Manufacture"
            value={modelDetail.manufacture}
            style={styles.gridItem}
          />
          <InfoPair
            label="Model Name"
            value={modelDetail.modelName}
            style={styles.gridItem}
          />
        </View>
        {(oldDetail.manufacture || oldDetail.modelName || oldDetail.year) && (
          <Text style={styles.subsectionTitle}>Old Model Detail</Text>
        )}
        <View style={styles.grid}>
          {oldDetail.manufacture && (
            <InfoPair
              label="Manufacture"
              value={oldDetail.manufacture}
              style={styles.gridItem}
            />
          )}
          {oldDetail.modelName && (
            <InfoPair
              label="Model Name"
              value={oldDetail.modelName}
              style={styles.gridItem}
            />
          )}
          {oldDetail.year && (
            <InfoPair
              label="Year"
              value={oldDetail.year}
              style={styles.gridItem}
            />
          )}
        </View>

        <Text style={styles.sectionTitle}>
          Follow-up / Remarks ({communicationLog.length})
        </Text>
        <View style={styles.table}>
          <View style={styles.tableRow} fixed>
            <Text style={[styles.tableColHeader, { width: "13%" }]}>Date</Text>
            <Text style={[styles.tableColHeader, { width: "15%" }]}>
              Type/Channel
            </Text>
            <Text style={[styles.tableColHeader, { width: "17%" }]}>
              Employee
            </Text>
            <Text style={[styles.tableColHeader, { width: "40%" }]}>
              Discussion/Remark{" "}
            </Text>
            <Text style={[styles.tableColHeader, { width: "15%" }]}>
              Next Follow-up
            </Text>
          </View>
          {communicationLog.slice(0, 10).map((comm, i) => (
            <View
              key={i}
              style={i % 2 === 0 ? styles.tableRow : styles.tableRowAlt}
              wrap={false}
            >
              <Text style={[styles.tableCol, { width: "13%" }]}>
                {formatDate(comm.date)}
              </Text>
              <Text style={[styles.tableCol, { width: "15%" }]}>
                {comm.type}{" "}
                <Text style={styles.smallText}>({comm.channel})</Text>
              </Text>
              <Text style={[styles.tableCol, { width: "17%" }]}>
                {comm.agent}
              </Text>
              <Text style={[styles.tableCol, { width: "40%" }]}>
                {comm.summary}
              </Text>
              <Text style={[styles.tableCol, { width: "15%" }]}>
                {formatDate(comm.nextFollowUpDate)}
              </Text>
            </View>
          ))}
          {communicationLog.length === 0 && (
            <View style={styles.tableRow}>
              <Text
                style={[
                  styles.tableCol,
                  { width: "100%", textAlign: "center", padding: 8 },
                ]}
              >
                No follow-up or remark records.
              </Text>
            </View>
          )}
        </View>

        <View break />
        <Text style={styles.sectionTitle}>
          Deliveries & Purchases ({deliveries.length})
        </Text>
        {deliveries.map((delivery, index) => {
          const delPricing = delivery.pricing || {};
          const oldProdExch = delPricing.oldProductDetailsForExchange || {};
          const delPayment = delivery.paymentDetails || {};
          const delLoan = delPayment.loanDetails || {};
          const delCheck = delPayment.checkDetails || {};
          const delProd = delivery.productDetails || {};
          const delSalesperson = delivery.deliverySalesperson || {};

          return (
            <View
              key={delivery.deliveryId}
              style={styles.deliverySection}
              wrap={false}
            >
              <Text
                style={[
                  styles.subsectionTitle,
                  {
                    color: "#16A085",
                    borderBottomColor: "#16A085",
                    fontSize: 11,
                  },
                ]}
              >
                Delivery #{index + 1} - ID: {delivery.deliveryId} (Date:{" "}
                {formatDate(delivery.deliveryDate)})
              </Text>
              <View style={styles.grid}>
                <InfoPair
                  label="Retail Date"
                  value={formatDate(delivery.retailDate)}
                  style={styles.gridItem}
                />
                <InfoPair
                  label="Del. Salesperson"
                  value={`${delSalesperson.name || ""} (${
                    delSalesperson.employeeId || ""
                  })`}
                  style={styles.gridItem}
                />
              </View>
              <Text style={styles.subsectionTitle}>Product Delivered</Text>
              <InfoPair label="Manufacturer" value={delProd.manufacturerName} />
              <InfoPair
                label="Model"
                value={`${delProd.modelName || ""} (${
                  delProd.yearOfManufacture || ""
                }, ${delProd.color || ""})`}
              />
              <InfoPair label="Chassis No." value={delProd.chassisNumber} />
              <InfoPair label="Engine No." value={delProd.engineNumber} />
              <Text style={styles.subsectionTitle}>Pricing & Exchange</Text>
              <InfoPair
                label="New Product Price"
                value={formatCurrency(delPricing.price)}
                valueStyle={styles.currency}
              />
              {delPricing.exchangeOldProduct &&
                (oldProdExch.make || oldProdExch.model) && (
                  <View style={styles.subtleHighlight}>
                    <InfoPair
                      label="Exchanged"
                      value={`${oldProdExch.make || ""} ${
                        oldProdExch.model || ""
                      } (Reg: ${oldProdExch.registrationNumber || "N/A"})`}
                    />
                    <InfoPair
                      label="Exchange Value"
                      value={formatCurrency(oldProdExch.oldPrice)}
                      valueStyle={styles.currency}
                    />
                  </View>
                )}
              <InfoPair
                label="Price After Exchange"
                value={formatCurrency(delPricing.remainingPrice)}
                valueStyle={styles.currency}
              />
              {delPricing.accessories && delPricing.accessories.length > 0 && (
                <>
                  <Text style={styles.subsectionTitle}>Accessories</Text>
                  {delPricing.accessories.map((acc) => (
                    <InfoPair
                      key={acc.name}
                      label={acc.name}
                      value={formatCurrency(acc.price)}
                      valueStyle={styles.currency}
                    />
                  ))}
                </>
              )}
              <InfoPair
                label="Total Payable"
                value={formatCurrency(delPricing.totalAmountPayable)}
                valueStyle={[styles.currency, { fontFamily: "Helvetica-Bold" }]}
              />
              <Text style={styles.subsectionTitle}>
                Payment Details (for this Delivery)
              </Text>
              <InfoPair label="Payment Mode" value={delPayment.mode} />
              {delPayment.cashAmount > 0 && (
                <InfoPair
                  label="Cash Paid"
                  value={formatCurrency(delPayment.cashAmount)}
                  valueStyle={styles.currency}
                />
              )}
              {(delLoan.bankName || delLoan.loanAmount) && (
                <View style={styles.notesBox}>
                  <Text style={styles.label}>Loan: </Text>
                  <Text>
                    {delLoan.bankName || ""}, Acc:{" "}
                    {delLoan.loanAccountNumber || ""}, Amt:{" "}
                    {formatCurrency(delLoan.loanAmount)}, Sanctioned:{" "}
                    {formatDate(delLoan.sanctionDate)}
                  </Text>
                </View>
              )}
              {(delCheck.bankName || delCheck.checkNumber) && (
                <View style={styles.notesBox}>
                  <Text style={styles.label}>Check: </Text>
                  <Text>
                    {delCheck.bankName || ""} ({delCheck.branch || ""}), No:{" "}
                    {delCheck.checkNumber || ""}, Date:{" "}
                    {formatDate(delCheck.checkDate)}, Amt:{" "}
                    {formatCurrency(delCheck.checkAmount)}
                  </Text>
                </View>
              )}
              {delPayment.transactionSummary && (
                <View style={styles.notesBox}>
                  <Text style={styles.smallText}>
                    <Text style={styles.label}>Summary: </Text>
                    {delPayment.transactionSummary}
                  </Text>
                </View>
              )}
              {delivery.deliveryNotes && (
                <View
                  style={[
                    styles.notesBox,
                    { marginTop: 3, backgroundColor: "#E8F6F3" },
                  ]}
                >
                  <Text style={styles.smallText}>
                    <Text style={styles.label}>Delivery Notes: </Text>
                    {delivery.deliveryNotes}
                  </Text>
                </View>
              )}
            </View>
          );
        })}
        {deliveries.length === 0 && (
          <Text style={styles.smallText}>
            No delivery records found for this customer.
          </Text>
        )}

        <Text style={styles.sectionTitle}>
          Equipment Serviced by Us ({servicedEquipment.length})
        </Text>
        <View style={styles.table}>
          <View style={styles.tableRow} fixed>
            <Text style={[styles.tableColHeader, { width: "30%" }]}>Model</Text>
            <Text style={[styles.tableColHeader, { width: "25%" }]}>
              Serial/Chassis
            </Text>
            <Text style={[styles.tableColHeader, { width: "15%" }]}>
              Purchased
            </Text>
            <Text style={[styles.tableColHeader, { width: "15%" }]}>
              Last Service
            </Text>
            <Text style={[styles.tableColHeader, { width: "15%" }]}>
              Op. Hours
            </Text>
          </View>
          {servicedEquipment.map((eq, i) => (
            <View
              key={eq.id}
              style={i % 2 === 0 ? styles.tableRow : styles.tableRowAlt}
              wrap={false}
            >
              <Text style={[styles.tableCol, { width: "30%" }]}>
                {eq.modelName}
              </Text>
              <Text style={[styles.tableCol, { width: "25%" }]}>
                {eq.serialNumber}
              </Text>
              <Text style={[styles.tableCol, { width: "15%" }]}>
                {formatDate(eq.purchaseDate)}
              </Text>
              <Text style={[styles.tableCol, { width: "15%" }]}>
                {formatDate(eq.lastServiceDate)}
              </Text>
              <Text
                style={[styles.tableCol, { width: "15%", textAlign: "right" }]}
              >
                {eq.hoursOperated || "N/A"}
              </Text>
            </View>
          ))}
          {servicedEquipment.length === 0 && (
            <View style={styles.tableRow}>
              <Text
                style={[
                  styles.tableCol,
                  { width: "100%", textAlign: "center", padding: 8 },
                ]}
              >
                No equipment service records with us.
              </Text>
            </View>
          )}
        </View>
        {customerExistingFleet && customerExistingFleet.length > 0 && (
          <>
            <Text style={styles.sectionTitle}>
              Customer's Other Existing Equipment (
              {customerExistingFleet.length})
            </Text>
            <View style={styles.table}>
              <View style={styles.tableRow} fixed>
                <Text style={[styles.tableColHeader, { width: "25%" }]}>
                  Make
                </Text>
                <Text style={[styles.tableColHeader, { width: "25%" }]}>
                  Model
                </Text>
                <Text style={[styles.tableColHeader, { width: "12%" }]}>
                  Year
                </Text>
                <Text
                  style={[
                    styles.tableColHeader,
                    { width: "13%", textAlign: "right" },
                  ]}
                >
                  Hours
                </Text>
                <Text style={[styles.tableColHeader, { width: "25%" }]}>
                  Notes
                </Text>
              </View>
              {customerExistingFleet.map((eq, i) => (
                <View
                  key={eq.id}
                  style={i % 2 === 0 ? styles.tableRow : styles.tableRowAlt}
                  wrap={false}
                >
                  <Text style={[styles.tableCol, { width: "25%" }]}>
                    {eq.make}
                  </Text>
                  <Text style={[styles.tableCol, { width: "25%" }]}>
                    {eq.model}
                  </Text>
                  <Text style={[styles.tableCol, { width: "12%" }]}>
                    {eq.yearBought}
                  </Text>
                  <Text
                    style={[
                      styles.tableCol,
                      { width: "13%", textAlign: "right" },
                    ]}
                  >
                    {eq.approxTotalHours}
                  </Text>
                  <Text style={[styles.tableCol, { width: "25%" }]}>
                    {eq.notes}
                  </Text>
                </View>
              ))}
            </View>
          </>
        )}
        <Text style={styles.sectionTitle}>
          Service History ({serviceHistory.length})
        </Text>
        <View style={styles.table}>
          <View style={styles.tableRow} fixed>
            <Text style={[styles.tableColHeader, { width: "13%" }]}>Date</Text>
            <Text style={[styles.tableColHeader, { width: "17%" }]}>
              Equipment
            </Text>
            <Text style={[styles.tableColHeader, { width: "22%" }]}>
              Service
            </Text>
            <Text style={[styles.tableColHeader, { width: "30%" }]}>
              Tech & Notes
            </Text>
            <Text
              style={[
                styles.tableColHeader,
                { width: "10%", textAlign: "right" },
              ]}
            >
              Cost
            </Text>
            <Text style={[styles.tableColHeader, { width: "8%" }]}>
              Next Due
            </Text>
          </View>
          {serviceHistory.map((s, i) => (
            <View
              key={s.serviceId}
              style={i % 2 === 0 ? styles.tableRow : styles.tableRowAlt}
              wrap={false}
            >
              <Text style={[styles.tableCol, { width: "13%" }]}>
                {formatDate(s.serviceDate)}
              </Text>
              <Text style={[styles.tableCol, { width: "17%" }]}>
                {s.equipmentModel}
              </Text>
              <Text style={[styles.tableCol, { width: "22%" }]}>
                {s.serviceType}
              </Text>
              <Text style={[styles.tableCol, { width: "30%" }]}>
                <Text style={styles.label}>Tech:</Text> {s.technician}{" "}
                <Text style={styles.smallText}>({s.notes})</Text>
              </Text>
              <Text
                style={[styles.tableCol, { width: "10%", textAlign: "right" }]}
              >
                {formatCurrency(s.cost)}
              </Text>
              <Text style={[styles.tableCol, { width: "8%", fontSize: 7 }]}>
                {formatDate(s.nextServiceDueDate)}
              </Text>
            </View>
          ))}
          {serviceHistory.length === 0 && (
            <View style={styles.tableRow}>
              <Text
                style={[
                  styles.tableCol,
                  { width: "100%", textAlign: "center", padding: 8 },
                ]}
              >
                No service history records.
              </Text>
            </View>
          )}
        </View>
        <Text style={styles.sectionTitle}>Account Summary & Preferences</Text>
        <View style={styles.grid}>
          <View style={styles.gridItem}>
            <Text style={styles.subsectionTitle}>Financial Snapshot</Text>
            <InfoPair
              label="Account Balance"
              value={formatCurrency(cd.accountBalance)}
              valueStyle={cd.accountBalance > 0 ? styles.highlight : null}
            />
            <InfoPair
              label="Last Invoice"
              value={formatDate(cd.lastInvoiceDate)}
            />
            <InfoPair
              label="Last Payment"
              value={formatDate(cd.lastPaymentDate)}
            />
          </View>
          <View style={styles.gridItem}>
            <Text style={styles.subsectionTitle}>Marketing Preferences</Text>
            <InfoPair
              label="Newsletter"
              value={marketingPreferences.newsletter ? "Yes" : "No"}
            />
            <InfoPair
              label="Product News"
              value={marketingPreferences.newProductAnnouncements}
            />
            <InfoPair
              label="Service Reminders"
              value={marketingPreferences.serviceReminders}
            />
            <InfoPair
              label="Event Invites"
              value={marketingPreferences.eventInvitations ? "Yes" : "No"}
            />
          </View>
        </View>
        {customerPaymentPreferences.preferredModeForService && (
          <>
            <Text style={styles.subsectionTitle}>
              Customer Payment Preferences
            </Text>
            <InfoPair
              label="Preferred for Service"
              value={customerPaymentPreferences.preferredModeForService}
            />
            {customerPaymentPreferences.bankDetailsForRefunds && (
              <InfoPair
                label="Bank for Refunds"
                value={`${
                  customerPaymentPreferences.bankDetailsForRefunds.bankName ||
                  ""
                } A/C: ${
                  customerPaymentPreferences.bankDetailsForRefunds
                    .accountNumber || ""
                }`}
              />
            )}
          </>
        )}
        {cd.customerNotes && (
          <>
            <Text style={styles.sectionTitle}>General Customer Notes</Text>
            <View style={styles.notesBox}>
              <Text>{cd.customerNotes}</Text>
            </View>
          </>
        )}
      </Page>
    </Document>
  );
};

export default CrmPdfDocument;
